import React, { useMemo } from 'react';
import { lookupValveSettings } from './lookupValveSettings';

const toneClass = {
  ok: 'bg-emerald-600',
  caution: 'bg-amber-500',
  stop: 'bg-red-600',
  empty: 'bg-pvs-accent',
};

const FittingTable = ({ title, rows }) => {
  if (!rows?.length) return null;
  return (
    <div className="space-y-2">
      <h3 className="text-white font-semibold text-sm">{title}</h3>
      <div className="overflow-x-auto rounded-lg border border-pvs-border">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-pvs-text-muted">
            <tr>
              <th className="text-left font-medium px-3 py-2">Pos.</th>
              <th className="text-left font-medium px-3 py-2">Fitting</th>
              <th className="text-left font-medium px-3 py-2">Item no.</th>
              <th className="text-left font-medium px-3 py-2">Torque</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={`${row.position}-${row.itemNo}-${i}`} className="border-t border-pvs-border/60">
                <td className="px-3 py-2 text-pvs-text-muted align-top whitespace-nowrap">{row.position || '—'}</td>
                <td className="px-3 py-2 text-white align-top">
                  <div>{row.name}</div>
                  {row.details ? (
                    <div className="text-xs text-pvs-text-dim mt-1">{row.details}</div>
                  ) : null}
                  {row.torque?.note ? (
                    <div className="text-xs text-pvs-text-dim mt-1">{row.torque.note}</div>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-white align-top font-mono whitespace-nowrap">{row.itemNo || '—'}</td>
                <td className="px-3 py-2 text-white align-top whitespace-nowrap">
                  {row.torque?.nm != null ? (
                    <div>
                      <span className="font-semibold">{row.torque.nm} Nm</span>
                      {row.torque.orientationTurns ? (
                        <div className="text-xs text-pvs-text-dim">
                          {row.torque.orientationTurns} turn to orient
                        </div>
                      ) : null}
                    </div>
                  ) : row.torque?.note ? (
                    <span className="text-xs text-pvs-text-dim">See method</span>
                  ) : (
                    <span className="text-pvs-text-dim">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const mentionsNationalEngineering = (result) => {
  if (result.contactNationalEngineering) return true;
  const texts = [
    result.title,
    result.body,
    ...(result.fitNow?.rows || []).map((row) => row.details),
    ...(result.existing?.rows || []).map((row) => row.details),
  ]
    .filter(Boolean)
    .join(' ');
  return /national engineering/i.test(texts);
};

const ValveSettingsModal = ({ typeCode, onClose, onOpenLineOfContact }) => {
  const result = useMemo(() => lookupValveSettings(typeCode), [typeCode]);
  const headerClass = toneClass[result.tone] || toneClass.stop;
  const type = result.type;
  const showLineOfContact = mentionsNationalEngineering(result);

  const openLineOfContact = () => {
    onClose();
    if (typeof onOpenLineOfContact === 'function') {
      onOpenLineOfContact();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black bg-opacity-70" onClick={onClose} />
      <div className="relative pvs-glass border border-pvs-border rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className={`${headerClass} px-6 py-4 flex items-start justify-between gap-3`}>
          <div>
            <h2 className="text-pvs-bg text-lg font-bold">{result.title}</h2>
            {type ? (
              <p className="text-pvs-bg/90 text-sm mt-1">
                Type {result.code}
                {type.name ? ` — ${type.name}` : ''}
                {type.capacity ? ` · ${type.capacity}` : ''}
                {result.layout ? ` · ${result.layout}` : ''}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-pvs-bg text-2xl leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 overflow-y-auto">
          <p className="text-white text-sm leading-relaxed">{result.body}</p>

          {type?.alternatives?.length ? (
            <p className="text-sm text-pvs-text-muted">
              Alternative tanks in VID: {type.alternatives.join(', ')}
            </p>
          ) : null}

          {result.ok ? (
            <>
              <FittingTable title={result.fitNow?.label} rows={result.fitNow?.rows} />
              <FittingTable title={result.existing?.label} rows={result.existing?.rows} />
            </>
          ) : null}

          {showLineOfContact ? (
            <div className="rounded-lg border border-pvs-accent/40 bg-pvs-accent/10 px-4 py-3 space-y-3">
              <p className="text-sm text-white">
                Contact Calor National Engineering via the Calor Line of Contact page.
              </p>
              <button
                type="button"
                onClick={openLineOfContact}
                className="px-4 py-2 rounded-lg bg-pvs-accent text-pvs-bg font-semibold text-sm hover:bg-pvs-accent-dim"
              >
                Open Calor Line of Contact
              </button>
            </div>
          ) : null}

          <div className="rounded-lg border border-pvs-border bg-black/20 px-4 py-3 space-y-2">
            <p className="text-xs font-semibold text-pvs-text-muted uppercase tracking-wide">
              Jointing (VID Section 10)
            </p>
            <ul className="list-disc pl-5 text-xs text-pvs-text-dim space-y-1">
              {(result.notes || []).map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-pvs-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-pvs-text-dim italic">
            {result.source.document} · Issue {result.source.issue} · {result.source.date}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim self-end sm:self-auto"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ValveSettingsModal;
